import type {AxiosPromise} from 'axios'
import Axios from 'axios'
import type {IJSONObjectConvertible, TProgressEventHandler} from '~/api/client/types'
import {toJSON} from '~/api/client/types'

const defaultHeaders = {
  'content-type': 'application/json',
  'accept': 'application/json; charset=utf-8',
}

export interface IAxiosHTTPClient {
  post: (
    url: string,
    params: IJSONObjectConvertible,
    signal?: AbortSignal
  ) => AxiosPromise<any>
  get: (url: string, signal?: AbortSignal) => AxiosPromise<any>
  // A body-carrying read: sent like `post` but over the idempotent QUERY method.
  query: (
    url: string,
    params: IJSONObjectConvertible,
    signal?: AbortSignal
  ) => AxiosPromise<any>
  delete: (
    url: string,
    params: IJSONObjectConvertible,
    signal?: AbortSignal
  ) => AxiosPromise<any>
  // This is specifically about S3, not more generic, because S3 upload URLs are signed by the server
  // So they can't have e.g. arbitrary headers added to them
  uploadToS3: (
    url: string,
    binary: Blob,
    headers: any,
    signal?: AbortSignal,
    uploadProgressHandler?: TProgressEventHandler
  ) => AxiosPromise<any>
  // Used for e.g. sending a file to the backend for image conversion
  processBlob: (
    url: string,
    blob: Blob,
    signal?: AbortSignal,
    uploadProgressHandler?: TProgressEventHandler
  ) => AxiosPromise<Blob>
}

export const axiosHTTPClient = (host: string): IAxiosHTTPClient => ({
  post: (endpoint: string, params = {}, signal?: AbortSignal) =>
    Axios({
      url: `${host}${endpoint}`,
      method: 'post',
      data: toJSON(params),
      headers: defaultHeaders,
      withCredentials: true,
      signal,
    }),
  get: (endpoint: string, signal?: AbortSignal) =>
    Axios({
      url: `${host}${endpoint}`,
      method: 'get',
      headers: defaultHeaders,
      withCredentials: true,
      signal,
    }),
  query: (endpoint: string, params = {}, signal?: AbortSignal) =>
    Axios({
      url: `${host}${endpoint}`,
      method: 'QUERY',
      data: toJSON(params),
      headers: defaultHeaders,
      withCredentials: true,
      signal,
    }),
  delete: (endpoint, params = {}, signal?: AbortSignal) =>
    Axios({
      url: `${host}${endpoint}`,
      method: 'delete',
      data: toJSON(params),
      headers: defaultHeaders,
      withCredentials: true,
      signal,
    }),
  uploadToS3: (
    url: string,
    binary: Blob,
    headers: any,
    signal?: AbortSignal,
    uploadProgressHandler?: TProgressEventHandler
  ) =>
    Axios({
      url,
      method: 'put',
      data: binary,
      headers,
      signal,
      onUploadProgress: uploadProgressHandler,
    }),
  processBlob: (
    endpoint: string,
    blob: Blob,
    signal?: AbortSignal,
    uploadProgressHandler?: TProgressEventHandler
  ) => {
    return Axios({
      url: `${host}${endpoint}`,
      method: 'post',
      data: blob,
      headers: {
        'accept': '*/*',
        // Note: If not set, a default form content type is used
        // This causes the server to not read the body correctly.
        'Content-Type': 'application/octet-stream',
      },
      signal,
      withCredentials: true,
      onUploadProgress: uploadProgressHandler,
      responseType: 'blob',
    })
  },
})
